import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, ArrowRight, Check, Sparkles, Zap, ShieldCheck, Truck,
  CreditCard, Layers, Plug, Banknote, ShoppingBag, Code2, ChevronRight,
  Boxes, LineChart, Globe, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { VerqoMark } from "../components/VerqoMark";

// ──────────────────────────────────────────────────────────────────────────
// verqo Marketing site — redesigned. Stripe-grade composition: bold typography,
// generous whitespace, dark inverted hero panels, electric-lime spot color,
// no decorative imagery, only structural mocks made of Tailwind primitives.
// ──────────────────────────────────────────────────────────────────────────

const ease = [0.22, 1, 0.36, 1];
const fadeUp = { initial: { opacity: 0, y: 18 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.7, ease } };
const stagger = { whileInView: { transition: { staggerChildren: 0.08 } }, viewport: { once: true, margin: "-80px" } };

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Nav />
      <Hero />
      <OsManifesto />
      <Pillars />
      <Platform />
      <Builders />
      <AILoop />
      <IntegrationsStrip />
      <Pricing />
      <CTA />
      <Footer />
    </div>
  );
}

// ── Section: OS Manifesto (full-width dark slab between Hero & Pillars) ───
// Mirrors the deck's positioning so first-time visitors anchor on "operating
// system" before scrolling into the product pillars. Same dark-charcoal +
// lime aesthetic as the rest of the brand.
function OsManifesto() {
  return (
    <section className="relative bg-foreground text-background border-y border-foreground" data-testid="os-manifesto">
      {/* Subtle lime glow on the right */}
      <div className="pointer-events-none absolute -right-20 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-accent/15 blur-3xl"/>
      <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 relative">
        <motion.div {...fadeUp} className="max-w-4xl">
          <div className="inline-flex items-center gap-2 text-[10.5px] mono uppercase tracking-[0.25em] text-accent">
            <span className="w-1.5 h-1.5 rounded-full bg-accent"/>
            The positioning
          </div>
          <h2 className="display font-semibold mt-5 text-4xl sm:text-5xl lg:text-[72px] tracking-[-0.02em] leading-[0.98]">
            The <span className="text-accent">next-gen</span> operating system<br/>
            for <span className="italic font-normal text-background/55">wholesale</span>.
          </h2>
          <p className="mt-7 max-w-2xl text-[16.5px] sm:text-[18px] text-background/70 leading-relaxed">
            Buyers, sellers, lenders and insurers — running their day-to-day on one platform, in real time.
            <span className="text-background"> Installable apps. Open APIs. Bank-grade rails.</span> The way wholesale is supposed to work.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3" data-testid="os-manifesto-tiles">
            {[
              { l: "5 surfaces",   v: "Buyer · Seller · Lender · Insurer · Bank" },
              { l: "1 backbone",   v: "One ledger · one identity · one truth" },
              { l: "Apps",         v: "Catalog · Dropship · RFQ · ERP · custom" },
              { l: "APIs",         v: "REST · webhooks · OAuth · SDK" },
            ].map(({ l, v }) => (
              <div key={l} className="rounded-2xl border border-background/15 px-4 py-3">
                <div className="text-[9.5px] mono uppercase tracking-[0.2em] text-accent">{l}</div>
                <div className="mt-1 text-[12.5px] text-background/85">{v}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────
function Nav() {
  return (
    <nav className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-foreground/10">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <VerqoMark className="w-7 h-7 rounded-md overflow-hidden"/>
          <span className="display font-semibold text-[17px] tracking-tight">verqo</span>
        </Link>
        <div className="hidden md:flex items-center gap-7 text-[13.5px] text-foreground/65">
          <a href="#platform" className="hover:text-foreground transition-colors" data-testid="nav-platform">Platform</a>
          <a href="#pillars" className="hover:text-foreground transition-colors" data-testid="nav-pillars">Products</a>
          <a href="#builders" className="hover:text-foreground transition-colors" data-testid="nav-builders">Build on verqo</a>
          <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-pricing">Pricing</a>
        </div>
        <div className="flex items-center gap-2.5">
          <Link to="/login" data-testid="nav-login" className="text-[13.5px] text-foreground/80 hover:text-foreground px-2">Sign in</Link>
          <Link to="/register" data-testid="nav-register">
            <Button className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-9 px-4 text-[13px]">
              Get started <ArrowRight className="w-3.5 h-3.5 ml-1"/>
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative">
      <div className="max-w-7xl mx-auto px-6 pt-24 sm:pt-32 pb-20 lg:pb-24 relative">
        <motion.div {...fadeUp}
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-foreground/20 bg-foreground text-background text-[10.5px] mono uppercase tracking-[0.2em]"
          data-testid="hero-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_10px_var(--accent,#e8ff3a)]"/>
          The next-gen operating system for wholesale
        </motion.div>
        <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }}
          className="display font-semibold mt-6 max-w-5xl text-5xl sm:text-6xl lg:text-[88px] tracking-[-0.03em] leading-[0.97]">
          The infrastructure<br/>for <span className="italic font-normal text-foreground/65">wholesale</span>.
        </motion.h1>
        <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }}
          className="mt-6 max-w-2xl text-[17px] sm:text-[19px] text-foreground/65 leading-relaxed">
          Payments. Credit. Shipping. Insurance. Every wholesale business runs on the same plumbing —
          we built it once so you don't have to. Plug in, extend with apps, ship in days.
        </motion.p>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-9 flex flex-wrap items-center gap-3">
          <Link to="/register" data-testid="hero-cta-start">
            <Button className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-12 px-6 text-[14px]">
              Start free <ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </Link>
          <span className="text-[12.5px] text-foreground/45 ml-2 hidden sm:inline">No card required · 60-second setup</span>
        </motion.div>

        {/* Hero structural mock — bento "what's running" board */}
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="mt-20 lg:mt-24">
          <DashboardMock />
        </motion.div>
      </div>

      {/* Subtle dotted backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(10,31,28,0.06) 1px, transparent 0)", backgroundSize: "28px 28px" }}/>
    </section>
  );
}

// ── Hero structural dashboard mock (no images — Tailwind primitives only) ─
function DashboardMock() {
  return (
    <div className="rounded-[28px] border border-foreground/10 bg-white shadow-[0_50px_140px_-40px_rgba(10,31,28,0.18)] p-3" data-testid="hero-mock">
      <div className="flex items-center gap-2 px-2 pb-3 border-b border-foreground/10">
        <span className="w-2.5 h-2.5 rounded-full bg-foreground/15"/>
        <span className="w-2.5 h-2.5 rounded-full bg-foreground/15"/>
        <span className="w-2.5 h-2.5 rounded-full bg-foreground/15"/>
        <div className="ml-3 chip text-[10px] mono uppercase tracking-widest border bg-secondary/60">app.verqo.com / dashboard</div>
        <div className="ml-auto chip text-[10px] mono uppercase tracking-widest bg-accent text-foreground">LIVE</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3">
        {/* Big card */}
        <div className="md:col-span-7 rounded-2xl bg-foreground text-background p-6 relative overflow-hidden">
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-accent">Pre-approved · AI-underwritten</div>
          <div className="display text-5xl mt-3">$50,000</div>
          <div className="text-[12.5px] text-background/70 mt-1">Net-30 · 94% confidence · auto-granted in 60s</div>
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[["Used", "$18,963"], ["Available", "$31,038"], ["Util.", "37.9%"]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-background/10 px-3 py-2">
                <div className="text-[9.5px] mono uppercase tracking-widest text-background/55">{l}</div>
                <div className="display text-base mt-0.5">{v}</div>
              </div>
            ))}
          </div>
          <div className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full bg-accent/15 blur-3xl"/>
        </div>
        {/* AI memo */}
        <div className="md:col-span-5 rounded-2xl border border-foreground/10 p-5 bg-secondary/30">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-accent fill-accent"/>
            <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/55">AI credit memo</div>
          </div>
          <p className="text-[12.5px] mt-2 leading-relaxed">
            <strong>Demo Wholesale Co.</strong> · 4-yr CA LLC, SOS active, no liens, $480K avg monthly revenue, 0 NSFs in 24mo. <span className="text-emerald-700 font-medium">Recommend $50K Net-30, 94% confidence.</span>
          </p>
        </div>
        {/* 3 mini status tiles */}
        {[
          { l: "ACH transfer · settled", v: "+$12,400", tone: "ok" },
          { l: "Verqo Shipping label · USPS", v: "in transit", tone: "info" },
          { l: "Plaid · cashflow refresh", v: "✓ 2s ago", tone: "ok" },
        ].map((t, i) => (
          <div key={i} className="md:col-span-4 rounded-2xl border border-foreground/10 p-4 bg-white">
            <div className="text-[9.5px] mono uppercase tracking-[0.2em] text-foreground/50">{t.l}</div>
            <div className="display text-lg mt-1">{t.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section: 4 Pillars ────────────────────────────────────────────────────
function Pillars() {
  const items = [
    { Icon: CreditCard, label: "Payments",  desc: "Cards, ACH, wire — settled into a real ledger.",  meta: "Modern Treasury" },
    { Icon: Banknote,   label: "Credit",    desc: "AI-underwritten Net-30 to Net-90 in 60 seconds.", meta: "Plaid · Middesk · Verqo AI" },
    { Icon: Truck,      label: "Shipping",  desc: "Parcel + LTL/FTL freight from one API.",          meta: "Verqo Freight Network" },
    { Icon: ShieldCheck,label: "Insurance", desc: "Trade credit insurance baked into checkout.",     meta: "Coface (coming)" },
  ];
  return (
    <section id="pillars" className="border-t border-foreground/10 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <motion.div {...fadeUp} className="max-w-2xl">
          <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-foreground/50">The four pillars</div>
          <h2 className="display text-4xl sm:text-5xl tracking-[-0.02em] mt-3 leading-[1.05]">
            Everything wholesale needs.<br/><span className="italic font-normal text-foreground/65">In one stack.</span>
          </h2>
        </motion.div>
        <motion.div {...stagger} className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map(({ Icon, label, desc, meta }) => (
            <motion.div key={label} variants={fadeUp}
              data-testid={`pillar-${label.toLowerCase()}`}
              className="group rounded-2xl border border-foreground/10 bg-white p-6 hover:bg-foreground hover:text-background transition-all duration-500">
              <Icon className="w-5 h-5 text-foreground/55 group-hover:text-accent transition-colors"/>
              <div className="display text-2xl mt-12 tracking-[-0.01em]">{label}</div>
              <p className="text-[13.5px] text-foreground/65 group-hover:text-background/65 mt-2 leading-relaxed transition-colors">{desc}</p>
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-foreground/40 group-hover:text-background/40 mt-6 transition-colors">{meta}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Section: Platform (the dark inverted "everything connects" panel) ─────
function Platform() {
  return (
    <section id="platform" className="bg-foreground text-background border-y border-foreground">
      <div className="max-w-7xl mx-auto px-6 py-28">
        <motion.div {...fadeUp} className="max-w-3xl">
          <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-accent">The platform</div>
          <h2 className="display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] mt-3 leading-[1.04]">
            One backbone.<br/>Every workflow.
          </h2>
          <p className="text-[16.5px] text-background/70 mt-6 leading-relaxed max-w-xl">
            From buyer signup to seller payout — orders, invoices, financing, shipping, insurance and reporting all share one ledger, one identity layer, one source of truth.
          </p>
        </motion.div>

        <motion.div {...stagger} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "Orders",        v: "Unified" },
            { l: "Invoicing",     v: "Auto-generated" },
            { l: "Wallet",        v: "Ledger-backed" },
            { l: "Credit",        v: "AI-underwritten" },
            { l: "Identity",      v: "KYB + KYC" },
            { l: "Shipping",      v: "Smart-routed" },
            { l: "Insurance",     v: "Embedded" },
            { l: "Apps",          v: "Extensible" },
          ].map(({ l, v }) => (
            <motion.div key={l} variants={fadeUp}
              className="rounded-2xl border border-background/15 px-5 py-7">
              <div className="text-[10px] mono uppercase tracking-[0.2em] text-background/50">{l}</div>
              <div className="display text-2xl mt-2 tracking-tight">{v}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Section: Build on verqo (Shopify-app-store angle) ─────────────────────
function Builders() {
  return (
    <section id="builders" className="border-b border-foreground/10">
      <div className="max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div {...fadeUp}>
          <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-foreground/50">Build on verqo</div>
          <h2 className="display text-4xl sm:text-5xl tracking-[-0.02em] mt-3 leading-[1.05]">
            We're the rails.<br/><span className="italic font-normal text-foreground/65">You build the train.</span>
          </h2>
          <p className="text-[16px] text-foreground/65 mt-6 leading-relaxed">
            Every wholesale business has its own quirks. verqo ships the core — payments, credit, shipping, insurance — and lets you (or the community) build apps for everything else: dropship, RFQ, PO generation, custom catalogs, ERP sync, anything.
          </p>
          <div className="mt-8 space-y-2.5 text-[13.5px]">
            {[
              "Public REST + Webhook APIs for every domain object",
              "App Store install model — apps run inside seller's portal",
              "OAuth scopes per app · revoke in one click",
              "Build private apps for your team, or publish to the marketplace",
            ].map((s) => (
              <div key={s} className="flex items-start gap-2.5">
                <Check className="w-3.5 h-3.5 mt-1 text-accent shrink-0"/> <span className="text-foreground/75">{s}</span>
              </div>
            ))}
          </div>
          <div className="mt-9 flex gap-3">
            <Link to="/register" data-testid="builders-cta-start">
              <Button className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 px-5 text-[13.5px]">
                Start building <ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </Link>
            <a href="#pricing" className="self-center text-[13px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1">
              Read the docs <ArrowUpRight className="w-3.5 h-3.5"/>
            </a>
          </div>
        </motion.div>

        {/* Right-side: stylized API + apps mock */}
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
          <div className="rounded-2xl border border-foreground/10 bg-foreground text-background overflow-hidden shadow-[0_30px_80px_-30px_rgba(10,31,28,0.35)]">
            <div className="px-5 py-3 border-b border-background/10 flex items-center gap-2 text-[11px] mono">
              <span className="w-2 h-2 rounded-full bg-accent"/>
              <span className="text-background/55">POST</span>
              <span className="text-background/85">api.verqo.com/v1/orders</span>
            </div>
            <pre className="p-5 text-[12px] mono leading-relaxed text-background/85 overflow-x-auto">{`{
  "buyer_id":  "buy_8f4c9a",
  "lines": [
    { "sku": "SKU-1042",  "qty": 240,  "unit": 12.50 }
  ],
  "payment": "net30",
  "shipping": "auto",
  "insurance": true
}`}</pre>
            <div className="px-5 py-4 border-t border-background/10 grid grid-cols-3 gap-2 text-[10.5px] mono">
              <Tag color="accent">200 OK</Tag>
              <Tag>credit · approved</Tag>
              <Tag>label · USPS GA</Tag>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            {[
              { l: "Dropship",    n: "12 apps" },
              { l: "RFQ",         n: "5 apps" },
              { l: "ERP sync",    n: "8 apps" },
              { l: "Catalog",     n: "11 apps" },
              { l: "Reporting",   n: "9 apps" },
              { l: "AI",          n: "6 apps" },
            ].map(({ l, n }) => (
              <div key={l} className="rounded-xl border border-foreground/10 bg-white px-4 py-3">
                <div className="text-[10px] mono uppercase tracking-widest text-foreground/45">{l}</div>
                <div className="display text-base mt-1">{n}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Tag({ color = "muted", children }) {
  const c = color === "accent"
    ? "bg-accent text-foreground"
    : "bg-background/10 text-background/85 border border-background/15";
  return <span className={`inline-flex items-center px-2 py-1 rounded-md ${c}`}>{children}</span>;
}

// ── Section: AI Loop (the magic story) ────────────────────────────────────
function AILoop() {
  const steps = [
    { n: "01", title: "Buyer signs up",       desc: "5-step onboarding · KYB form + Plaid bank connect."},
    { n: "02", title: "AI underwrites",       desc: "Middesk + Plaid signals → Verqo AI writes a structured credit memo." },
    { n: "03", title: "Auto-grant fires",     desc: "Confidence ≥ 0.85 → buyer is pre-approved automatically." },
    { n: "04", title: "Buyer places order",   desc: "Net-30 from second one. No paperwork. No human in the loop." },
  ];
  return (
    <section className="border-b border-foreground/10 bg-secondary/30">
      <div className="max-w-7xl mx-auto px-6 py-28">
        <motion.div {...fadeUp} className="max-w-3xl">
          <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-foreground/50">The autonomous loop</div>
          <h2 className="display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] mt-3 leading-[1.04]">
            Net-30 in <span className="italic">60 seconds</span>.<br/>Without an underwriter.
          </h2>
        </motion.div>
        <motion.div {...stagger} className="mt-14 grid md:grid-cols-4 gap-3 relative">
          {steps.map((s, i) => (
            <motion.div key={s.n} variants={fadeUp}
              className="rounded-2xl border border-foreground/10 bg-white p-6 relative">
              <div className="text-[10.5px] mono tracking-[0.2em] text-accent font-medium">{s.n}</div>
              <div className="display text-xl mt-4 tracking-tight">{s.title}</div>
              <p className="text-[13px] text-foreground/65 mt-2 leading-relaxed">{s.desc}</p>
              {i < steps.length - 1 && <ChevronRight className="hidden md:block absolute right-[-22px] top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/20 z-10"/>}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Section: Integrations strip (real partners as text-marquee) ───────────
function IntegrationsStrip() {
  const partners = [
    "Plaid", "Modern Treasury", "Middesk", "Verqo Freight",
    "Verqo AI", "Sentry", "Stripe", "Coface (soon)",
  ];
  return (
    <section className="py-16 border-b border-foreground/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center text-[10.5px] mono uppercase tracking-[0.3em] text-foreground/45 mb-10">
          Powered by best-in-class infrastructure
        </div>
      </div>
      <Marquee speed={32} gradient gradientColor="rgb(248,247,242)" gradientWidth={120} pauseOnHover>
        <div className="flex items-center gap-12 px-6 text-foreground/60">
          {[...partners, ...partners].map((p, i) => (
            <span key={`${p}-${i}`} data-testid={`partner-${p.toLowerCase().replace(/\W+/g, "-")}`}
              className="display text-2xl tracking-tight whitespace-nowrap hover:text-foreground transition-colors">
              {p}
            </span>
          ))}
        </div>
      </Marquee>
    </section>
  );
}

// ── Section: Pricing ──────────────────────────────────────────────────────
function Pricing() {
  const tiers = [
    {
      name: "Starter", price: "$99", suffix: "/mo", lede: "For founder-led brands just starting wholesale",
      bullets: [
        "2 active buyers included · then $30/buyer/mo",
        "Unlimited orders + catalog",
        "Full Payments · Credit · Shipping · Insurance",
        "1 owner seat",
        "Custom domain + white-label",
      ],
      txnPricing: {
        card:    "4.0%",
        instant: "0.50%",
      },
      cta: "Join waitlist",
    },
    {
      name: "Growth", price: "$599", suffix: "/mo", lede: "For wholesale brands with a team running one brand", featured: true,
      bullets: [
        "10 active buyers included · then $25/buyer/mo",
        "5 team seats",
        "Email support (biz hours)",
        "Branded buyer storefront",
        "Custom invoice & email templates",
      ],
      txnPricing: {
        card:    "3.0%",
        instant: "0.50%",
      },
      cta: "Join waitlist",
    },
    {
      name: "Pro", price: "$1,499", suffix: "/mo", lede: "For scaled wholesale operators with multi-channel ops",
      bullets: [
        "50 active buyers included · then $20/buyer/mo",
        "20 team seats",
        "Advanced reporting + dashboards",
        "Multi-warehouse + multi-region",
        "Dedicated onboarding specialist",
        "API access (standard rate limits)",
      ],
      txnPricing: {
        card:    "3.0%",
        instant: "0.30%",
      },
      cta: "Join waitlist",
    },
    {
      name: "Enterprise", price: "$3,999", suffix: "/mo", lede: "For multi-brand or multi-warehouse operations",
      bullets: [
        "100 active buyers included · then $15/buyer/mo",
        "Unlimited team seats",
        "SSO + custom contract pricing",
        "Live chat + phone support (24/7)",
        "Dedicated CSM + 3 onboarding calls",
        "Scaled API + AI demand forecasting",
      ],
      txnPricing: {
        card:    "3.0%",
        instant: "0.30%",
      },
      cta: "Talk to us",
    },
  ];
  return (
    <section id="pricing" className="border-b border-foreground/10">
      <div className="max-w-7xl mx-auto px-6 py-28">
        <motion.div {...fadeUp} className="max-w-2xl">
          <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-foreground/50">Pricing</div>
          <h2 className="display text-4xl sm:text-5xl tracking-[-0.02em] mt-3 leading-[1.05]">
            Pay for the <span className="italic">scale</span>, not the seats.
          </h2>
        </motion.div>
        <motion.div {...stagger} className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {tiers.map((t) => (
            <motion.div key={t.name} variants={fadeUp}
              data-testid={`tier-${t.name.toLowerCase()}`}
              className={`rounded-2xl border p-7 ${t.featured ? "bg-foreground text-background border-foreground" : "bg-white border-foreground/10"}`}>
              <div className={`text-[10.5px] mono uppercase tracking-[0.25em] ${t.featured ? "text-accent" : "text-foreground/50"}`}>{t.name}</div>
              <div className="mt-5 flex items-baseline gap-1">
                <div className="display text-5xl tracking-[-0.02em]">{t.price}</div>
                {t.suffix && <span className={`text-sm ${t.featured ? "text-background/55" : "text-foreground/55"}`}>{t.suffix}</span>}
              </div>
              <p className={`text-[13px] mt-2 ${t.featured ? "text-background/65" : "text-foreground/65"}`}>{t.lede}</p>
              <div className="my-6 h-px bg-current opacity-10"/>
              <ul className="space-y-2.5 text-[13px]">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <Check className={`w-3.5 h-3.5 mt-1 shrink-0 ${t.featured ? "text-accent" : "text-foreground/55"}`}/>
                    <span className={t.featured ? "text-background/85" : "text-foreground/75"}>{b}</span>
                  </li>
                ))}
              </ul>
              {/* Per-plan transaction pricing rates (Feb 2026 — tiered).
                  Flat-rate rails (ACH $0.50 / Wire $15 / etc) are the
                  same on every plan, so we only surface the rails that
                  actually differ — card + instant payout. Full table on
                  /pricing for the curious. */}
              <div className={`mt-5 rounded-lg px-3 py-2.5 ${t.featured ? "bg-background/10" : "bg-foreground/[0.04]"}`}>
                <div className={`text-[9.5px] mono uppercase tracking-[0.18em] ${t.featured ? "text-background/55" : "text-foreground/50"}`}>
                  Transaction rates
                </div>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <div>
                    <div className={`text-[10px] ${t.featured ? "text-background/55" : "text-foreground/55"}`}>Credit card</div>
                    <div className={`text-[14px] font-semibold tracking-tight ${t.featured ? "text-background" : "text-foreground"}`}>{t.txnPricing.card}</div>
                  </div>
                  <div>
                    <div className={`text-[10px] ${t.featured ? "text-background/55" : "text-foreground/55"}`}>Instant payout</div>
                    <div className={`text-[14px] font-semibold tracking-tight ${t.featured ? "text-background" : "text-foreground"}`}>{t.txnPricing.instant}</div>
                  </div>
                </div>
                <Link to="/pricing" className={`inline-block mt-2 text-[10.5px] underline underline-offset-2 ${t.featured ? "text-background/70 hover:text-background" : "text-foreground/55 hover:text-foreground"}`} data-testid={`tier-pricing-link-${t.name.toLowerCase()}`}>
                  See all rates →
                </Link>
              </div>
              <Link to={t.name === "Platform" ? "#" : "/register"} data-testid={`tier-cta-${t.name.toLowerCase()}`} className="block mt-7">
                <Button className={`w-full rounded-full h-11 text-[13.5px] ${t.featured ? "bg-accent text-foreground hover:bg-accent/90" : "bg-foreground text-background hover:bg-foreground/90"}`}>
                  {t.cta} <ArrowRight className="w-3.5 h-3.5 ml-1"/>
                </Button>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Section: Final CTA ────────────────────────────────────────────────────
function CTA() {
  return (
    <section className="border-b border-foreground/10">
      <div className="max-w-7xl mx-auto px-6 py-28">
        <motion.div {...fadeUp}
          className="rounded-3xl bg-foreground text-background p-12 lg:p-20 relative overflow-hidden">
          <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-accent">Get started</div>
          <h2 className="display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] mt-4 leading-[1.05] max-w-3xl">
            Stop assembling your stack.<br/><span className="italic">Start running your business.</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="cta-start-final">
              <Button className="rounded-full bg-accent text-foreground hover:bg-accent/90 h-12 px-6 text-[14px]">
                Start free <ArrowRight className="w-4 h-4 ml-1"/>
              </Button>
            </Link>
            <Link to="/login" data-testid="cta-signin-final">
              <Button variant="outline" className="rounded-full h-12 px-6 border-background/20 text-background hover:bg-background/10 text-[14px]">
                Sign in
              </Button>
            </Link>
          </div>
          <div className="absolute -right-20 -bottom-32 w-[480px] h-[480px] rounded-full bg-accent/15 blur-[120px]"/>
        </motion.div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { h: "Platform",   items: ["Payments", "Credit", "Shipping", "Insurance", "Wallet"] },
    { h: "Build",      items: ["API Reference", "App Store", "Webhooks", "OAuth", "Examples"] },
    { h: "Company",    items: ["About", "Customers", "Careers", "Press", "Contact"] },
    { h: "Resources",  items: ["Docs", "Blog", "Changelog", "Status", "Security"] },
  ];
  return (
    <footer className="bg-background">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-[1.4fr_2fr_2fr_2fr_2fr] gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <VerqoMark className="w-7 h-7 rounded-md overflow-hidden"/>
              <span className="display font-semibold text-[17px] tracking-tight">verqo</span>
            </div>
            <p className="text-[12.5px] text-foreground/55 mt-3 leading-relaxed max-w-[180px]">
              Wholesale, instant.
            </p>
          </div>
          {cols.map((c) => (
            <div key={c.h}>
              <div className="text-[10.5px] mono uppercase tracking-[0.25em] text-foreground/45">{c.h}</div>
              <ul className="mt-4 space-y-2 text-[13.5px] text-foreground/70">
                {c.items.map((i) => <li key={i}><a href="#" className="hover:text-foreground transition-colors">{i}</a></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 pt-6 border-t border-foreground/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[12px] text-foreground/55">
          <div>© 2026 verqo Inc. · All rights reserved</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-foreground">Privacy</a>
            <a href="#" className="hover:text-foreground">Terms</a>
            <a href="#" className="hover:text-foreground">Security</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
